import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { MapPin, Heart, MessageCircle, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfile = () => {
  const { userId } = useParams();
  const { token } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const sendConnectionRequest = async () => {
    try {
      await axios.post(
        `${API}/connections/request`,
        { target_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Connection request sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8 pt-24 text-center">
          <p className="text-gray-600 text-lg">User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8 pt-24">
        <div className="glass-dark rounded-3xl p-8 shadow-2xl" data-testid="user-profile">
          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-8">
            <Avatar className="w-32 h-32 border-4 border-teal shadow-xl">
              <AvatarImage src={user.profile_photo} />
              <AvatarFallback className="bg-white text-black text-4xl font-bold">
                {user.name.charAt(0)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">{user.name}</h1>
              {user.location?.city && (
                <div className="flex items-center justify-center sm:justify-start space-x-1 text-gray-600 mb-4">
                  <MapPin className="w-4 h-4" />
                  <span>{user.location.city}{user.location.country && `, ${user.location.country}`}</span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="connect-user-btn"
                  onClick={sendConnectionRequest}
                  className="bg-white text-black border-2 border-teal hover:bg-teal hover:text-white rounded-full font-semibold"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Connect
                </Button>
                <Button
                  data-testid="message-user-btn"
                  onClick={() => navigate(`/messages/${userId}`)}
                  variant="outline"
                  className="border-2 border-teal text-teal hover:bg-teal hover:text-white rounded-full"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message
                </Button>
              </div>
            </div>
          </div>

          {user.bio && (
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-2">About</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{user.bio}</p>
            </div>
          )}

          {user.interests && user.interests.length > 0 && (
            <div>
              <h2 className="text-xl font-bold mb-3">Interests</h2>
              <div className="flex flex-wrap gap-2">
                {user.interests.map((interest, idx) => (
                  <Badge key={idx} className="bg-gradient-to-r from-peach to-gold text-gray-900 font-semibold border-none px-4 py-2">
                    <Heart className="w-3 h-3 mr-1" />
                    {interest}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;